/**
 * kisiBaglantilari.js — Kişi Listesi'nin YENİ SEKMEDE açılan bağlantılarının adresleri (backlog #273, #283).
 *
 * Yeni sekme router state'i taşımaz; #262'nin "Kişi Listesine Dön" bağlantısı state'ten kurulduğu için
 * burada kaynak sayfa adreste gider: `geri` (liste adresi, süzgeç/arama/sayfa dahil — #262'den beri hepsi
 * adreste) + `geriEtiket`. Kişi sayfası (PersonelDetayPage) önce state'e, yoksa bu parametreye bakar;
 * ikisi de yoksa eski varsayılana (firmanın ekip sayfası) düşer.
 *
 * Kişi sayfası: /Management/Kisi/Personeller/{personId}?fuarId=&tab=&alt=&belge=&vurgu=   (#365: firma bağlamı yok;
 *   eski /Management/Firmalar/Personel/{firmaId}/{personId} rotası aynı sayfayı açar, firmaId yok sayılır)
 *   Sekmeler (#363 #367 #368): belgeler (İSG Belgeleri; `alt` = kisisel | meslek | firma) · giris (Giriş Kartı) ·
 *   firmalar · kimlik (Kontroller: kimlik doğrulama, KVKK, KKD, Özel İzin, yasak) · kullanicilar.
 *   KVKK / KKD / Kimlik → ?tab=kimlik&vurgu=<kvkk|kkd|kimlik> (kartı çerçeveler, #481)  ·
 *   Mesleki Eğitim → ?tab=belgeler&alt=meslek&belge=meslekiEgitim  ·  diğer belgeler → ?tab=belgeler&belge=<anahtar>
 *   (`belge` kutuyu çerçeveler ve dosyası varsa inceleme penceresini açar; `vurgu` yalnızca çerçeveler.)
 *   Eski anahtarlar `sekmeCozumle` ile çözülür: ?tab=meslek → belgeler/meslek, ?tab=detaylar → kimlik.
 * Firma belgeleri: /Management/Firmalar/Profil/{firmaId}?tab=belgeler&fuarId=  (KartUygunsuzluklari ile aynı hedef)
 */

/** Kişi sayfasının sekme anahtarları, `?tab=` ile saklanır. */
import i18n from "../../i18n";

export const KISI_SEKMELERI = ["belgeler", "giris", "firmalar", "kimlik", "kullanicilar"];
/** `belgeler` sekmesinin alt sekmeleri, `?alt=` ile saklanır. */
export const BELGE_ALT_SEKMELERI = ["kisisel", "meslek", "firma"];

/**
 * `?tab=` / `?alt=` değerlerini geçerli sekmeye çevirir; #284/#137'den kalan eski bağlantılar (yeni sekmede açılan
 * liste kareleri, Kart Yazdır bağlantıları) bozulmasın: `meslek` → belgeler + meslek, `detaylar` → kimlik.
 */
export function sekmeCozumle(tab, alt) {
  if (tab === "meslek") return { tab: "belgeler", alt: "meslek" };
  if (tab === "detaylar") return { tab: "kimlik", alt: null };
  const gecerliTab = KISI_SEKMELERI.includes(tab) ? tab : "belgeler";
  const gecerliAlt = gecerliTab === "belgeler" && BELGE_ALT_SEKMELERI.includes(alt) ? alt : null;
  return { tab: gecerliTab, alt: gecerliAlt };
}

/**
 * #481: Kişi sayfasında tıklanan şeyin yükleme alanını saran kırmızı çerçeve (#273'ün çerçevesi; bu temada primary kırmızı).
 * PersonelBelgeKutusu / MeslekiEgitimKarti aynı sınıfları kendi içinde yazar; Kontroller kartları buradan alır.
 */
export const VURGU_SINIFI = "border border-2 border-primary shadow";

/**
 * Belge karesi anahtarı → kişi sayfasındaki sekme (+ alt sekme, belge, vurgu).
 * `belge` = o belgenin kutusu çerçevelenir VE dosyası varsa inceleme penceresi açılır (#481: dosyası olsa da çerçevelenir);
 * `vurgu` = yalnızca çerçeve (KVKK/KKD/Kimlik kartları: KVKK'nın dosyası varken pencere açmak istenmiyor).
 */
function belgeHedefi(docKey) {
  if (docKey === "kvkk" || docKey === "kkd" || docKey === "kimlik") return { tab: "kimlik", vurgu: docKey };
  // #734: the passport / ID image box lives on Kontroller, next to the Kimlik doğrulama section (it was under İSG Belgeleri › Meslek).
  if (docKey === "kimlikFotografi") return { tab: "kimlik", belge: docKey };
  // #363: Mesleki Eğitim'in yüklemesi İSG Belgeleri › Meslek alt sekmesinde (dosyası varsa inceleme penceresi her sekmede açılır).
  if (docKey === "meslekiEgitim") return { tab: "belgeler", alt: "meslek", belge: docKey };
  return { tab: "belgeler", belge: docKey };
}

/**
 * @param docKey  verilirse o belgenin sekmesi/penceresi; verilmezse yalnızca `tab` (varsayılan "belgeler")
 */
export function kisiSayfasiUrl({ firmaId, personId, fuarId, docKey, tab, geri, geriEtiket }) {
  const params = new URLSearchParams();
  if (fuarId) params.set("fuarId", String(fuarId));
  const hedef = docKey ? belgeHedefi(docKey) : { tab: tab || "belgeler" };
  params.set("tab", hedef.tab);
  if (hedef.alt) params.set("alt", hedef.alt);
  if (hedef.belge) params.set("belge", hedef.belge);
  if (hedef.vurgu) params.set("vurgu", hedef.vurgu);
  if (geri) {
    params.set("geri", geri);
    if (geriEtiket) params.set("geriEtiket", geriEtiket);
  }
  return `${kisiYolu(firmaId, personId)}?${params.toString()}`;
}

/** Kişi sayfasının yolu: firma varsa eski firmalı rota, yoksa firmasız rota (#292). */
function kisiYolu(firmaId, personId) {
  return firmaId
    ? `/Management/Firmalar/Personel/${firmaId}/${personId}`
    : `/Management/Kisi/Personeller/${personId}`;
}

/**
 * Kişi sayfasında firma/fuar bağlamını değiştirir (#292, #266): sekme ve "geri" parametreleri korunur, belge
 * parametresi düşer (başka bağlamda aynı belge penceresi anlamsız). `firmaId` null → firmasız görünüm.
 */
export function kisiBaglamUrl({ personId, firmaId, fuarId, searchParams }) {
  const params = new URLSearchParams();
  ["tab", "alt", "geri", "geriEtiket"].forEach((k) => {
    const v = searchParams?.get(k);
    if (v) params.set(k, v);
  });
  if (fuarId) params.set("fuarId", String(fuarId));
  const sorgu = params.toString();
  return sorgu ? `${kisiYolu(firmaId, personId)}?${sorgu}` : kisiYolu(firmaId, personId);
}

/** Bir firmanın Belgeler sayfası (bağımsız Profil rotası; Firma Detay'daki gömülü sekme değil). */
export function firmaBelgeleriUrl({ firmaId, fuarId }) {
  const params = new URLSearchParams({ tab: "belgeler" });
  if (fuarId) params.set("fuarId", String(fuarId));
  return `/Management/Firmalar/Profil/${firmaId}?${params.toString()}`;
}

/**
 * Kişi sayfasının `geri` / `geriEtiket` parametrelerinden "geri dön" hedefi. YALNIZCA uygulama içi adres
 * kabul edilir (`/` ile başlayan, `//` ile başlamayan) — adres çubuğundan gelen değer dışarı yönlendirmesin.
 * @returns {{ from: string, fromLabel: string } | null}
 */
export function geriHedefiOku(searchParams) {
  const geri = searchParams.get("geri");
  if (!geri || !geri.startsWith("/") || geri.startsWith("//")) return null;
  const etiket = (searchParams.get("geriEtiket") || "").slice(0, 60);
  return { from: geri, fromLabel: etiket || i18n.t("components:kisiBaglantilari.geriDon") };
}
